"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.MoneyEventSchema = exports.MONEY_EVENT_KINDS = void 0;
exports.dayKey = dayKey;
exports.monthKey = monthKey;
exports.weekKey = weekKey;
exports.bucketRevenue = bucketRevenue;
exports.buildRevenueDigest = buildRevenueDigest;
exports.digestTrend = digestTrend;
const zod_1 = require("zod");
/**
 * REVENUE DIGEST — what actually moved, aggregated for a morning email.
 *
 * ---------------------------------------------------------------------------
 * CHARGED IS NOT DEPOSITED, AND CONFLATING THEM MISSTATES CASH
 * ---------------------------------------------------------------------------
 *
 * The single most important distinction in this file, and the one a digest gets
 * wrong by default:
 *
 *  - **charged** — a customer's invoice was paid. Stripe now holds the money.
 *  - **deposited** — Stripe paid out to the bank account. This LAGS the charge,
 *    typically by days, and is the only figure that is actually in the bank.
 *
 * A digest that adds up `invoice.paid` and labels the result "deposited" tells
 * an operator they have money they cannot yet spend, and the gap is not small
 * or constant — it moves with payout schedule, disputes and reserves. Because
 * these figures feed loan reporting (see `billingSchedule.ts`), overstating
 * cash is the expensive direction to be wrong in.
 *
 * So the two are separate fields, sourced from separate Stripe events
 * (`invoice.paid` vs `payout.paid`), and there is deliberately no combined
 * "revenue" total for a caller to reach for by mistake.
 *
 * ---------------------------------------------------------------------------
 * CALENDAR DAYS, IN ONE FIXED TIMEZONE
 * ---------------------------------------------------------------------------
 *
 * A "daily" digest is a WALL-CLOCK question — "what happened yesterday" — not a
 * UTC-midnight one. Aggregating in UTC puts a 7pm Pacific charge into the next
 * day's digest, so the numbers never match what an operator sees in Stripe's
 * dashboard set to their own timezone. Every bucketing function here takes an
 * explicit IANA timezone and there is no default: a digest silently bucketed in
 * the server's zone is a digest that changes when the server moves.
 */
// ---------------------------------------------------------------------------
// The events a digest is built from
// ---------------------------------------------------------------------------
exports.MONEY_EVENT_KINDS = [
    /** An invoice was paid. Stripe holds the money; it is not in the bank yet. */
    'charge',
    /** Money returned to a customer. Reduces the day's net. */
    'refund',
    /** A charge attempt failed. No money moved; counted so failures are visible. */
    'failure',
    /** Stripe paid out to the bank. THIS is cash in hand. */
    'payout',
];
exports.MoneyEventSchema = zod_1.z.object({
    kind: zod_1.z.enum(exports.MONEY_EVENT_KINDS),
    /** Unix ms the event occurred, as Stripe reported it. */
    at: zod_1.z.number(),
    /**
     * EX-TAX where the concept applies (a charge), for consistency with
     * `billingSchedule`'s reconciliation. A payout has no tax component.
     */
    subtotalCents: zod_1.z.number().int().default(0),
    taxCents: zod_1.z.number().int().default(0),
    /** Gross: what the customer was charged, or what landed in the bank. */
    amountCents: zod_1.z.number().int(),
    /** Stripe's processing fee, where known. Never inferred. */
    feeCents: zod_1.z.number().int().default(0),
    tenantId: zod_1.z.string().optional(),
    /** The reporting dimension — mirrors `BillingScheduleRow.dealCode`. */
    dealCode: zod_1.z.string().optional(),
    invoiceId: zod_1.z.string().optional(),
    payoutId: zod_1.z.string().optional(),
    currency: zod_1.z.string().default('usd'),
});
const emptyBucket = (key) => ({
    key,
    chargedCents: 0, chargedTaxCents: 0, chargeCount: 0,
    refundedCents: 0, refundCount: 0,
    failedCents: 0, failureCount: 0,
    depositedCents: 0, payoutCount: 0,
    feeCents: 0, netChargedCents: 0,
});
function addEvent(bucket, event) {
    switch (event.kind) {
        case 'charge':
            bucket.chargedCents += event.amountCents;
            bucket.chargedTaxCents += event.taxCents || 0;
            bucket.feeCents += event.feeCents || 0;
            bucket.chargeCount += 1;
            break;
        case 'refund':
            bucket.refundedCents += event.amountCents;
            bucket.refundCount += 1;
            break;
        case 'failure':
            bucket.failedCents += event.amountCents;
            bucket.failureCount += 1;
            break;
        case 'payout':
            bucket.depositedCents += event.amountCents;
            bucket.payoutCount += 1;
            break;
        default:
            break;
    }
    bucket.netChargedCents = bucket.chargedCents - bucket.refundedCents;
}
/**
 * `YYYY-MM-DD` for a timestamp, in a named timezone.
 *
 * Uses `Intl` with an explicit `timeZone` rather than any date arithmetic:
 * offsets change twice a year and a hand-rolled shift is wrong for one hour on
 * two days a year — which is exactly when a daily digest is scrutinised.
 */
function dayKey(at, timeZone) {
    const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone, year: 'numeric', month: '2-digit', day: '2-digit',
    }).formatToParts(new Date(at));
    const get = (t) => parts.find((p) => p.type === t)?.value ?? '';
    return `${get('year')}-${get('month')}-${get('day')}`;
}
/** `YYYY-MM` for a timestamp, in a named timezone. */
function monthKey(at, timeZone) {
    return dayKey(at, timeZone).slice(0, 7);
}
/**
 * The ISO week a timestamp falls in, as `YYYY-Www`.
 *
 * ISO weeks start on Monday and the week containing 4 January is week 1 — the
 * definition every finance system uses. A naive "day-of-year / 7" disagrees
 * with it in most years, which would make a weekly figure that never quite
 * matches an accountant's.
 */
function weekKey(at, timeZone) {
    const [y, m, d] = dayKey(at, timeZone).split('-').map(Number);
    const date = new Date(Date.UTC(y, m - 1, d));
    // Thursday of this week decides the year the week belongs to.
    const dayOfWeek = (date.getUTCDay() + 6) % 7; // Monday = 0
    date.setUTCDate(date.getUTCDate() - dayOfWeek + 3);
    const isoYear = date.getUTCFullYear();
    const firstThursday = new Date(Date.UTC(isoYear, 0, 4));
    const firstDayOfWeek = (firstThursday.getUTCDay() + 6) % 7;
    firstThursday.setUTCDate(firstThursday.getUTCDate() - firstDayOfWeek + 3);
    const week = 1 + Math.round((date.getTime() - firstThursday.getTime()) / (7 * 24 * 3600 * 1000));
    return `${isoYear}-W${String(week).padStart(2, '0')}`;
}
const keyFor = (grain, at, timeZone) => {
    if (grain === 'month')
        return monthKey(at, timeZone);
    if (grain === 'week')
        return weekKey(at, timeZone);
    return dayKey(at, timeZone);
};
/**
 * Bucket money events by day, week or month in a named timezone.
 *
 * Returned in ascending key order. A period with no events produces NO bucket —
 * a caller wanting a dense series fills gaps itself, because "no events" and
 * "zero" are the same number but not the same fact, and only the caller knows
 * which one its chart should show.
 */
function bucketRevenue(args) {
    const byKey = new Map();
    for (const event of args.events || []) {
        if (!event || typeof event.at !== 'number')
            continue;
        const key = keyFor(args.grain, event.at, args.timeZone);
        let bucket = byKey.get(key);
        if (!bucket) {
            bucket = emptyBucket(key);
            byKey.set(key, bucket);
        }
        addEvent(bucket, event);
    }
    return [...byKey.values()].sort((a, b) => a.key.localeCompare(b.key));
}
/**
 * The digest for ONE day, with its week- and month-to-date context.
 *
 * `asOf` is the moment the digest is generated; the day reported is the
 * calendar day containing it in `timeZone`. A caller running this at 06:00
 * local for "yesterday" passes yesterday's timestamp, not now — this function
 * deliberately does not guess which day was meant.
 *
 * `quiet` is surfaced rather than acted on. Whether a silent day is worth an
 * email is a product decision (an operator who gets nothing cannot tell "no
 * charges" from "the job failed"), and that decision belongs to the sender.
 */
function buildRevenueDigest(args) {
    const { events = [], asOf, timeZone } = args;
    const day = dayKey(asOf, timeZone);
    const week = weekKey(asOf, timeZone);
    const month = monthKey(asOf, timeZone);
    const today = emptyBucket(day);
    const weekToDate = emptyBucket(week);
    const monthToDate = emptyBucket(month);
    const lastWeekKeyValue = dayKey(asOf - 7 * 24 * 3600 * 1000, timeZone);
    const sameDayLastWeek = emptyBucket(lastWeekKeyValue);
    let sawLastWeek = false;
    const dealTotals = new Map();
    for (const event of events) {
        if (!event || typeof event.at !== 'number')
            continue;
        const eventDay = dayKey(event.at, timeZone);
        if (eventDay === day) {
            addEvent(today, event);
            if (event.kind === 'charge') {
                const key = event.dealCode || '';
                const entry = dealTotals.get(key) || { chargedCents: 0, chargeCount: 0, tenants: new Set() };
                entry.chargedCents += event.amountCents;
                entry.chargeCount += 1;
                if (event.tenantId)
                    entry.tenants.add(event.tenantId);
                dealTotals.set(key, entry);
            }
        }
        if (eventDay === lastWeekKeyValue) {
            addEvent(sameDayLastWeek, event);
            sawLastWeek = true;
        }
        // To-date windows include today, and only count events up to `asOf` — a
        // digest must never include a charge that happens later on the same day
        // than the moment it was generated.
        if (event.at <= asOf) {
            if (weekKey(event.at, timeZone) === week)
                addEvent(weekToDate, event);
            if (monthKey(event.at, timeZone) === month)
                addEvent(monthToDate, event);
        }
    }
    const byDeal = [...dealTotals.entries()]
        .map(([dealCode, v]) => ({
        dealCode, chargedCents: v.chargedCents, chargeCount: v.chargeCount, tenantCount: v.tenants.size,
    }))
        .sort((a, b) => b.chargedCents - a.chargedCents);
    return {
        day,
        timeZone,
        today,
        weekToDate,
        monthToDate,
        ...(sawLastWeek ? { sameDayLastWeek } : {}),
        byDeal,
        quiet: today.chargeCount === 0 && today.refundCount === 0
            && today.failureCount === 0 && today.payoutCount === 0,
    };
}
/**
 * Day-over-day movement against the same weekday a week ago.
 *
 * Compared with the SAME WEEKDAY rather than with yesterday, because
 * subscription billing is weekday-shaped — a Monday is not a Sunday, and
 * "down 60%" every Saturday is noise an operator learns to ignore.
 *
 * Returns `null` when there is no comparison point, rather than an invented
 * 100%: a percentage against a base of nothing is not information.
 */
function digestTrend(digest) {
    const base = digest.sameDayLastWeek;
    if (!base || base.chargedCents === 0)
        return null;
    const deltaCents = digest.today.chargedCents - base.chargedCents;
    return { deltaCents, percent: Math.round((deltaCents / base.chargedCents) * 100) };
}
//# sourceMappingURL=revenueDigest.js.map